import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  input,
} from '@angular/core';
import { UNLIMITED_PASSES } from '@fantaz/shared/config/src/lib/app.constants';
import { CurrentUser } from '@fantaz/shared/data-access/models';
import {
  CasualTournamentDetails,
  CasualTournamentEntity,
  EventSchedule,
  TournamentPass,
} from '@fantaz/shared/data-access/models/src/lib/tournament';
import { CurrencyService } from '@fantaz/shared/utils';

@Component({
  selector: 'fz-tournament-controls',
  templateUrl: './tournament-controls.component.html',
  styleUrls: ['./tournament-controls.component.scss'],
})
export class TournamentControlsComponent {
  /**
   * Inject currency service to format the price
   */
  #currencyService: CurrencyService = inject(CurrencyService);

  /**
   * Default pass used to display the price in the play button
   */
  @Input() activePass?: TournamentPass;

  @Input()
  entryAmount = 0.0;

  @Input()
  showJoinButton?: boolean;

  @Input()
  showMoreOptions?: boolean;

  @Input()
  vipVideo?: string | null;

  @Input()
  showPlayButton?: boolean;

  @Input()
  isEventPage?: boolean;

  @Input()
  playButtonLoading?: boolean | undefined | null;

  @Input()
  playButtonProcessing?: boolean;

  @Input()
  timesPlayed?: number | null;

  _enabledPlayButton = true;

  @Input()
  set enabledPlayButton(value: boolean | undefined | null) {
    if (value === null) {
      this._enabledPlayButton = true;
      return;
    }

    this._enabledPlayButton = typeof value !== 'undefined' ? value : true;
  }

  get enabledPlayButton(): boolean {
    return this._enabledPlayButton;
  }

  @Input()
  showTryForFreeButton?: boolean;

  @Input()
  showShareButton? = true;

  @Input()
  showPlayButtonAnother?: boolean;

  @Input()
  isGameOverPage?: boolean;

  @Input()
  showBecomeZboButton?: boolean;

  @Input()
  eventScheduler?: EventSchedule | undefined | null;

  @Input()
  casualGameStartTime: Date | string | null | undefined;

  @Input()
  casualGameEndTime: Date | string | null | undefined;

  @Input()
  tournament?: CasualTournamentEntity | undefined;

  @Input()
  tournamentDetails?: CasualTournamentDetails | undefined | null;

  @Input()
  user!: CurrentUser | null | undefined;

  @Input()
  userPass?: TournamentPass | null | undefined;

  @Input()
  passEntries: number;

  @Input()
  passCount?: number | null | undefined;

  //
  // config
  //
  @Input()
  shareMode: 'icon' | 'button' = 'icon';

  @Input()
  playText? = 'Play';

  analyticKeyPrefix = input<string>('');

  @Output()
  joinPress = new EventEmitter();

  @Output()
  playPress: EventEmitter<TournamentPass | null | undefined> =
    new EventEmitter();

  @Output()
  tryForFreePress = new EventEmitter();

  @Output()
  sharePress = new EventEmitter();

  @Output()
  playAnotherPress = new EventEmitter();

  @Output()
  promotionalVideo = new EventEmitter();

  @Input()
  responsive!: boolean;

  UNLIMITED_PASSES = UNLIMITED_PASSES;

  get isPassActive(): boolean {
    return false;
  }

  get isDisabled(): boolean {
    return (
      this.isRetired ||
      this.isFinished ||
      this.isCompleted ||
      !this.enabledPlayButton ||
      this.maximumEntriesReached ||
      this.playButtonProcessing == true
    );
  }

  get playButtonText(): string {
    let formattedPrice = this.#currencyService.convertDollar(this.entryAmount);

    if (this.isRetired) {
      return `Retired - ${formattedPrice} USD`;
    }

    if (this.isCompleted) {
      return `Completed - ${formattedPrice} USD`;
    }

    if (this.isFinished) {
      return `Ended - ${formattedPrice} USD`;
    }

    // In case of user not have a pass display the text with the price
    if (!this.activePass) {
      return `Play - ${formattedPrice} USD`;
    }

    const price = Number(this.activePass.price.price);
    formattedPrice = this.#currencyService.convertDollar(price);

    if (this.activePass.purchased) {
      // verify if entry is unlimited
      if (this.activePass.entries >= 5000) {
        return 'Play Unlimited';
      }

      if (this.activePass.entries == 1 || price == 0) {
        return `Play - ${formattedPrice} USD`;
      }

      const remain =
        this.activePass.entries - (this.activePass.activeCount || 0) + 1;
      return `Play - ${remain}/${this.activePass.entries}`;
    }

    if (price == 0) {
      return 'Play - $0.00 USD';
    }

    // check if exist a pass with only 1 entry

    return `Play - ${formattedPrice} USD`;
  }

  get startTime(): Date | null | undefined {
    if (typeof this.casualGameStartTime === 'string') {
      return new Date(this.casualGameStartTime);
    }

    if (this.tournament && this.tournament.casualGameStartTime) {
      return new Date(this.tournament.casualGameStartTime);
    }

    if (this.tournamentDetails && this.tournamentDetails.casualGameStartTime) {
      return new Date(this.tournamentDetails.casualGameStartTime);
    }

    return null;
  }

  get endTime(): Date | null | undefined {
    if (typeof this.casualGameEndTime === 'string') {
      return new Date(this.casualGameEndTime);
    }

    if (this.tournament && this.tournament.casualGameEndTime) {
      return new Date(this.tournament.casualGameEndTime);
    }

    if (this.tournamentDetails && this.tournamentDetails.casualGameEndTime) {
      return new Date(this.tournamentDetails.casualGameEndTime);
    }

    return this.casualGameEndTime;
  }

  get isComingSoon(): boolean {
    if (this.startTime) {
      if (this.startTime.getTime() > Date.now()) {
        return true;
      }
    }
    return false;
  }

  get isFinished(): boolean {
    if (this.endTime) {
      const date =
        typeof this.endTime === 'string'
          ? new Date(this.endTime)
          : this.endTime;
      if (date.getTime() < Date.now()) {
        return true;
      }
    }
    return false;
  }

  get isCompleted(): boolean {
    if (
      (this.tournament && this.tournament.isComplete) ||
      (this.tournamentDetails && this.tournamentDetails.isCompleted)
    ) {
      return true;
    }
    return false;
  }

  get isRetired(): boolean {
    if (this.eventScheduler) {
      return this.eventScheduler.retired;
    }

    return false;
  }

  get isLifeExperience(): boolean {
    if (this.tournament) {
      return this.tournament.lifeExperience;
    }

    if (this.tournamentDetails) {
      return this.tournamentDetails.lifeExperience as boolean;
    }

    return false;
  }

  get maximumEntriesReached() {
    const remainingEntries =
      (this.tournamentDetails?.timesCanEnter as number) -
      (this.timesPlayed || 0);
    return remainingEntries <= 0;
  }

  get maximumEntriesReachedTooltip() {
    if (this.maximumEntriesReached) {
      return `This tournament allows ${
        this.tournamentDetails?.timesCanEnter as number
      } ${
        (this.tournamentDetails?.timesCanEnter as number) > 1
          ? 'entries'
          : 'entry'
      }. You have reached the limit of how many times you can play in this tournament`;
    }
    return '';
  }

  /**
   * @deprecated remain pass count is not used anymore
   */
  get remainPassCount(): string {
    if (this.userPass) {
      const total = this.userPass.entries;
      const played =
        this.userPass?.entries - (this.userPass?.activeCount as number) + 1;
      return `${played}/${total}`;
    }

    return '';
  }

  onJoin() {
    this.joinPress.emit();
  }

  onPlay() {
    this.playPress.emit(this.activePass);
  }

  onTryForFree(): void {
    this.tryForFreePress.emit();
  }

  onShare() {
    this.sharePress.emit();
  }

  onPlayAnother() {
    this.playAnotherPress.emit();
  }

  becomeZBO(): void {
    window.location.href = `/profile/subscriptions`;
  }
  onWatchPromotionalVideo(): void {
    this.promotionalVideo.emit();
  }
}
